import React from 'react';
import { Copy, CheckCircle2 } from 'lucide-react';

export interface BankTransferDetailsProps {
  className?: string;
  recipient?: string;
  address?: string;
  bankName?: string;
  accountDisplay?: string;
  accountRaw?: string;
  ibanDisplay?: string;
  ibanRaw?: string;
  bic?: string;
  columns?: 'one' | 'two';
}

export function BankTransferDetails({
  className = '',
  recipient = 'Fundacja Znajdki',
  address = 'ul. Leona Berensona 90, 03-287 Warszawa',
  bankName = 'Bank PEKAO S.A.',
  accountDisplay = '91 1240 1082 1111 0010 4867 8484',
  accountRaw = '91124010821111001048678484',
  ibanDisplay = 'PL91 1240 1082 1111 0010 4867 8484',
  ibanRaw = 'PL91124010821111001048678484',
  bic = 'PKOPPLPW',
  columns = 'two'
}: BankTransferDetailsProps) {
  const [copiedField, setCopiedField] = React.useState<string | null>(null);

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  return (
    <div className={`bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg ${className}`}>
      <div className="mb-8">
        <h2 className="heading-3">Dane do przelewów</h2>
      </div>

      <div className={`grid ${columns === 'one' ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'} gap-8`}>
        <div>
          <h3 className="heading-4 mb-6">Przelew krajowy</h3>
          <div className="space-y-4">
            <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="label-medium mb-1">Odbiorca</p>
                  <p className="label-large">{recipient}</p>
                </div>
                <button
                  onClick={() => copyToClipboard(recipient, 'recipient')}
                  className="p-2 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 rounded-lg transition-colors"
                  title="Skopiuj odbiorcę"
                  aria-label="Skopiuj odbiorcę"
                >
                  {copiedField === 'recipient' ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <Copy className="w-5 h-5 text-accessible-gray-700 dark:text-white/90 high-contrast:text-white" />
                  )}
                </button>
              </div>
            </div>

            <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="label-medium mb-1">Adres</p>
                  <p className="label-large">{address}</p>
                </div>
                <button
                  onClick={() => copyToClipboard(address, 'address')}
                  className="p-2 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 rounded-lg transition-colors"
                  title="Skopiuj adres"
                  aria-label="Skopiuj adres"
                >
                  {copiedField === 'address' ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <Copy className="w-5 h-5 text-accessible-gray-700 dark:text-white/90 high-contrast:text-white" />
                  )}
                </button>
              </div>
            </div>

            <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="label-medium mb-1">Bank</p>
                  <p className="label-large">{bankName}</p>
                </div>
                <button
                  onClick={() => copyToClipboard(bankName, 'bank')}
                  className="p-2 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 rounded-lg transition-colors"
                  title="Skopiuj bank"
                  aria-label="Skopiuj bank"
                >
                  {copiedField === 'bank' ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <Copy className="w-5 h-5 text-accessible-gray-700 dark:text-white/90 high-contrast:text-white" />
                  )}
                </button>
              </div>
            </div>

            <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="label-medium mb-1">Numer konta</p>
                  <p className="heading-5 font-mono">{accountDisplay}</p>
                </div>
                <button
                  onClick={() => copyToClipboard(accountRaw, 'account')}
                  className="p-2 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 rounded-lg transition-colors"
                  title="Skopiuj numer konta"
                  aria-label="Skopiuj numer konta"
                >
                  {copiedField === 'account' ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <Copy className="w-5 h-5 text-accessible-gray-700 dark:text-white/90 high-contrast:text-white" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h3 className="heading-4 mb-6">Przelew zagraniczny</h3>
          <div className="space-y-4">
            <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="label-medium mb-1">IBAN</p>
                  <p className="heading-5 font-mono">{ibanDisplay}</p>
                </div>
                <button
                  onClick={() => copyToClipboard(ibanRaw, 'iban')}
                  className="p-2 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 rounded-lg transition-colors"
                  title="Skopiuj IBAN"
                  aria-label="Skopiuj IBAN"
                >
                  {copiedField === 'iban' ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <Copy className="w-5 h-5 text-accessible-gray-700 dark:text-white/90 high-contrast:text-white" />
                  )}
                </button>
              </div>
            </div>

            <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="label-medium mb-1">BIC/SWIFT</p>
                  <p className="heading-5 font-mono">{bic}</p>
                </div>
                <button
                  onClick={() => copyToClipboard(bic, 'bic')}
                  className="p-2 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 rounded-lg transition-colors"
                  title="Skopiuj BIC"
                  aria-label="Skopiuj BIC"
                >
                  {copiedField === 'bic' ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <Copy className="w-5 h-5 text-accessible-gray-700 dark:text-white/90 high-contrast:text-white" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BankTransferDetails;


